
import { computed, defineComponent, PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import { required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { useMutation } from "@vue/apollo-composable";
import { logErrorMessages } from "@vue/apollo-util";
import { useToast } from "vue-toastification";

import enUK from "@/locales/project/en-UK.json";

import { UPDATE_OFFERED, DELETE_OFFERED } from "@/graphql/offered/mutations";

import FormItem from "@/components/form/FormItem.vue";
import Modal from "@/components/Modal.vue";
import BasicButton from "@/components/BasicButton.vue";

import { Offered, Project } from "@/helpers/project/typesDefinition";
import { formatPrice, parsePriceToCents } from "@/helpers/utils";
import { DocumentNode } from "graphql";

type MessageSchema = typeof enUK;

export default defineComponent({
  props: {
    project: {
      type: Object as PropType<Project>,
      required: true,
    },
    offered: {
      type: Object as PropType<Offered>,
      required: true,
    },
    refetchQueries: {
      type: Array as PropType<{ query: DocumentNode; variables?: object }[]>,
      required: false,
      default: () => [],
    },
  },
  setup(props, context) {
    const { t } = useI18n<[MessageSchema], "en-UK">({
      useScope: "global",
      inheritLocale: true,
      messages: {
        "en-UK": enUK,
      },
    });

    const toast = useToast();

    const offeredId = ref(props.offered.id);
    const amountNet = ref(props.offered.amountNet);
    const date = ref(props.offered.date);
    const comment = ref(props.offered.comment);

    const updateVariables = computed(() => ({
      id: props.offered.id,
      amountNet: amountNet.value,
      date: date.value,
      comment: comment.value,
    }));

    const v$ = useVuelidate(
      {
        id: required,
        amountNet: required,
        date: required,
      },
      updateVariables,
      {
        $autoDirty: true,
      },
    );

    const {
      mutate: updateOffered,
      onDone: onDoneUpdateOffered,
      onError: onErrorUpdateOffered,
      loading: loadingUpdateOffered,
    } = useMutation(UPDATE_OFFERED, () => ({
      variables: updateVariables.value,
      refetchQueries: props.refetchQueries,
    }));

    onDoneUpdateOffered(async (result) => {
      if (result.data?.updateOffered?.status === "ok") {
        offeredId.value = result.data.updateOffered.data?.id;
        toast.success(t("success.offered.update"));
        context.emit("close");
      } else {
        const errorResponse = result.data?.updateOffered?.error;
        toast.error(t("errors.offered.update", { message: errorResponse }));
      }
    });

    onErrorUpdateOffered((error) => {
      logErrorMessages(error);
      toast.error(
        t("errors.offered.update", { message: t("errors.internal") }),
      );
    });

    const deleteVariables = computed(() => ({
      id: props.offered.id,
    }));

    const {
      mutate: deleteOffered,
      onDone: onDoneDeleteOffered,
      onError: onErrorDeleteOffered,
      loading: loadingDeleteOffered,
    } = useMutation(DELETE_OFFERED, () => ({
      variables: deleteVariables.value,
      refetchQueries: props.refetchQueries,
    }));

    onDoneDeleteOffered(async (result) => {
      if (result.data?.deleteOffered?.status === "ok") {
        offeredId.value = result.data.deleteOffered.data?.id;
        toast.success(t("success.offered.delete"));
        context.emit("close");
      } else {
        const errorResponse = result.data?.deleteOffered?.error;
        toast.error(t("errors.offered.delete", { message: errorResponse }));
      }
    });

    onErrorDeleteOffered((error) => {
      logErrorMessages(error);
      toast.error(
        t("errors.offered.delete", { message: t("errors.internal") }),
      );
    });

    const loading = computed(
      () => loadingDeleteOffered.value || loadingUpdateOffered.value,
    );

    return {
      loading,
      updateOffered,
      deleteOffered,
      formatPrice,
      parsePriceToCents,
      amountNet,
      date,
      comment,
      t,
      v$,
    };
  },
  emits: ["close"],
  methods: {
    async submitForm() {
      const isFormCorrect = await this.v$.$validate();
      if (isFormCorrect) {
        return this.updateOffered();
      }
    },
    closeModal(): void {
      this.$emit("close");
    },
  },
  components: {
    Modal,
    FormItem,
    BasicButton,
  },
});
